import { Button, Card, Col, Form, Input, Menu, MenuProps, Row, Space, TreeSelect } from 'antd';
import { SH } from 'common/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { TableContextProvider, useTableControls } from 'common/components/table/table-context';
import trafficService from '../../../../common/services/traffic-service/traffic-service';
import TableBase from 'common/components/table/table-base';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { directionOptions, reasonsOptions } from '../../constans/common.eiq-secure';
import { admittanceOptions } from 'common/constans/admittance.constants';
import { visitorTypeOptions } from 'common/constans/visitor.constants';
import { trafficInitialColumns } from './traffic-list.config';
import './traffic.scss';
import dayjs, { Dayjs } from 'dayjs';
import { ITrafficForm, ITrafficProps } from './traffic.types';
import { ReactComponent as ArrowRight } from 'assets/icons/arrow-right.svg';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import configurationStore from 'common/stores/configuration/configuration.store';
import featureStore from 'common/stores/features/feature.store';
import { FeatureName } from 'common/enums/feature-name.enum';
import { ITrafficRead } from 'common/services/traffic-service/traffic-service.types';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import { AdmittanceType } from 'common/enums/admittance-type.enum';
import exportService from 'common/services/export-service/export.service';
import { downloadCsv } from 'common/helpers/downloadCsv.helper';
import { useLocation } from 'react-router-dom';
import { DATE_MMMM_D_YYYY_H_MMA } from 'common/constans/date.constants';
import { debounce } from 'lodash';
import EiqDatePicker from 'common/components/date-picker/eiq-date-picker';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import TrafficReportExportModal from './traffic-report-export-modal/traffic-report-export-modal';
import { notification } from 'common/utils/notification';

const directionTreeOptions = [
  {
    title: 'All',
    value: 'SelectAll',
    children: directionOptions?.map((option) => ({ title: option.label, value: option.value })),
  },
];

const admittanceTreeOptions = [
  {
    title: 'All',
    value: 'SelectAll',
    children: admittanceOptions?.map((option) => ({ title: option.label, value: option.value })),
  },
];

const visitorTypeTreeOptions = [
  {
    title: 'All',
    value: 'SelectAll',
    children: visitorTypeOptions?.map((option) => ({ title: option.label, value: option.value })),
  },
];

const TABLE_NAME = 'traffic';

export const Traffic = ({ isArchive, basePath }: ITrafficProps) => {
  const initialValues = {
    dateTo: isArchive ? dayjs().subtract(configurationStore.trafficLogDaysToArchive, 'day') : dayjs(),
    dateFrom: isArchive ? dayjs().subtract(configurationStore.trafficLogDaysToArchive + 90, 'day') : dayjs().subtract(90, 'day'),
  };

  const [form] = Form.useForm();

  const { isMobile, isDesktop } = useMedia();

  const [cameraOptions, setCameraOptions] = useState<{ title: string; value: string; children: { title: string; value: string }[] }[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const debouncedFetchRef = useRef<((...args: any[]) => void) | null>(null);

  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(
      (resolve: (value: any) => void, pageLimit: any, filter: any, sort: any, search: any, isArchive: any) => {
        trafficService.getTraffic(pageLimit, filter, sort, search, isArchive).then(resolve);
      },
      500,
    );
  }

  const debounceDataFetch = useCallback((pageLimit: any, filter: any, sort: any, search: any, isArchive: any) => {
    return new Promise((resolve) => {
      debouncedFetchRef.current?.(resolve, pageLimit, filter, sort, search, isArchive);
    });
  }, []);

  const tableControls = useTableControls({
    onFetch: (pageLimit: any, filter: any, sort: any, search: any) => debounceDataFetch(pageLimit, filter, sort, search, isArchive),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME),
    initialColumns: filterColumnsByPersonalization(trafficInitialColumns, TABLE_NAME),
    onUpdateColumns: async (columns: any[], sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  const gateOptions = [
    {
      title: 'All',
      value: 'SelectAll',
      children: configurationStore?.configuration?.gates?.map((gate) => ({ title: gate.name, value: gate.name })),
    },
  ];

  const handleGateChange = (value: string[]) => {
    if (!showCamera) {
      return;
    }

    updateCameraOptions(value);
  };

  const updateCameraOptions = (selectedGates: string[]) => {
    let uniqueCameras = [];

    if (!selectedGates || selectedGates.length === 0 || selectedGates[0] === 'SelectAll') {
      const allCameras = configurationStore?.configuration?.gates?.flatMap((gate) => gate.cameras || []).map((camera) => camera.name);
      uniqueCameras = Array.from(new Set(allCameras));
    } else {
      const selectedGateNames = selectedGates;
      const selectedGatesData = configurationStore?.configuration?.gates?.filter((gate) => selectedGateNames.includes(gate.name));
      const cameras = selectedGatesData?.flatMap((gate) => gate.cameras || []).map((camera) => camera.name);
      uniqueCameras = Array.from(new Set(cameras));
    }

    const options = [{ title: 'All', value: 'SelectAll', children: uniqueCameras.map((camera) => ({ title: camera, value: camera })) }];
    setCameraOptions(options);

    const selectedCameras = form.getFieldValue('camera');
    if (selectedCameras && selectedCameras.length > 0 && selectedCameras[0] !== 'SelectAll') {
      const validCameraValues = uniqueCameras;
      const invalidCameras = selectedCameras.filter((camera: string) => !validCameraValues.includes(camera));

      if (invalidCameras.length > 0) {
        const newSelectedCameras = selectedCameras.filter((camera: string) => validCameraValues.includes(camera));
        form.setFieldsValue({ camera: newSelectedCameras });
      }
    }
  };

  useEffect(() => {
    const isLicensePlateRecognitionFeatureEnabled = featureStore.IsEnabled(FeatureName.LPR);

    setShowCamera(isLicensePlateRecognitionFeatureEnabled);

    let columns = trafficInitialColumns;

    if (isLicensePlateRecognitionFeatureEnabled) {
      const selectedGates = form.getFieldValue('gate');
      updateCameraOptions(selectedGates);
    } else {
      columns = columns.filter((column) => column.key !== 'camera');
    }

    if (!featureStore.isFastAccessEnabled) {
      columns = columns.filter((column) => column.key !== 'isEIQPassportUsed');
    }

    if (!featureStore.isDriveIdEnabled) {
      columns = columns.filter((column) => column.key !== 'idDocument');
    }

    if (!featureStore.isSmartPlateIdEnabled) {
      columns = columns.filter((column) => column.key !== 'smartPlateId');
    }

    if (columns.length !== trafficInitialColumns.length) {
      tableControls.updateColumns(columns);
    }
    setDateRangeFilter(initialValues.dateFrom, initialValues.dateTo);
  }, []);

  const setDateRangeFilter = (dateFrom?: dayjs.Dayjs, dateTo?: dayjs.Dayjs) => {
    const getDayStart = (date: Dayjs | undefined) => (date ? date.set('seconds', 0).set('milliseconds', 0).valueOf() : date);
    const getDayEnd = (date: Dayjs | undefined) => (date ? date.set('seconds', 59).set('milliseconds', 999).valueOf() : date);

    tableControls.updateFilter(
      'timestamp',
      [
        { operator: FilterTypesEnum.GreaterThanOrEqual, value: getDayStart(dateFrom), innerFilterType: 'AND' },
        { operator: FilterTypesEnum.LessThanOrEqual, value: getDayEnd(dateTo) },
      ],
      'AND',
    );
  };

  const onFinish = (value: ITrafficForm) => {
    // Select Tree
    if (value?.visitorType) {
      tableControls.updateFilter(
        'visitorType',
        value?.visitorType[0] === 'SelectAll' ? null : value?.visitorType?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    if (value?.admittanceType) {
      tableControls.updateFilter(
        'admittanceType',
        value?.admittanceType[0] === 'SelectAll'
          ? null
          : value?.admittanceType?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    if (value?.direction) {
      tableControls.updateFilter(
        'direction',
        value?.direction[0] === 'SelectAll' ? null : value?.direction?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    if (value?.gate) {
      tableControls.updateFilter(
        'gate',
        value?.gate[0] === 'SelectAll' ? null : value?.gate?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    if (value?.camera) {
      tableControls.updateFilter(
        'camera',
        value?.camera[0] === 'SelectAll' ? null : value?.camera?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    if (value?.turnBackReason) {
      tableControls.updateFilter(
        'turnBackReason',
        value?.turnBackReason[0] === 'SelectAll'
          ? null
          : value?.turnBackReason?.map((value) => ({ operator: FilterTypesEnum.Equals, value })),
        'AND',
      );
    }

    tableControls.updateFilter(
      'carLicensePlate',
      !!value?.carLicensePlate ? { operator: FilterTypesEnum.StartWith, value: value.carLicensePlate } : null,
      'AND',
    );

    // Date range
    if (value?.dateTo || value?.dateFrom) {
      setDateRangeFilter(value?.dateFrom, value?.dateTo);
    }
  };

  const getRowClassName = (rowIndex: number, rowData: ITrafficRead): string => {
    if (rowData.admittanceTypes.find((i) => i.admittanceType === AdmittanceType.Unidentified)) {
      return 'unlisted-guest-from-lrp';
    }
    return '';
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const getTableFilters = () => {
    const tableFilters = tableControls.getFilters();
    const tableSortBy = tableControls.getSortBy();
    const pageLimit = { page: 1, limit: 1_000_000 };

    return { tableFilters, tableSortBy, pageLimit };
  };

  const getFilterDateRange = (tableFilters: string | null) => {
    const getMatch = (regex: RegExp) => tableFilters?.match(regex)?.[0];

    const from = getMatch(/(?<=timestamp:>=)\d+/);
    const to = getMatch(/(?<=timestamp:<=)\d+/);

    return { from, to };
  };

  const gatFileName = (tableFilters: string | null) => {
    const formatDate = (ts: string) => dayjs(Number(ts)).format('MM-DD-YYYY');

    const { from, to } = getFilterDateRange(tableFilters);
    const community = configurationStore.configuration?.default.communityName;

    if (!isArchive) {
      const dateFrom = from ? formatDate(from) : dayjs().subtract(1, 'year').format('MM-DD-YYYY');
      const dateTo = to ? formatDate(to) : dayjs().format('MM-DD-YYYY');
      return `${community}-traffic-${dateFrom}-${dateTo}.csv`;
    }

    if (from && to) {
      return `${community}-traffic-archive-${formatDate(from)}-${formatDate(to)}.csv`;
    }

    return `${community}-traffic-archive.csv`;
  };

  const getColumns = () => {
    return (
      tableControls
        .columns!.filter((i) => !i.hidden)
        .map((c) => ({
          propertyName: c.dataKey!,
          displayName: c.title!,
        })) ?? []
    );
  };

  const sendTrafficCsvReportModal = useModal({
    onClose: async (isSuccess?: boolean, email?: string | null) => {
      if (!isSuccess) {
        return;
      }
      const { pageLimit, tableFilters, tableSortBy } = getTableFilters();
      const items = getColumns();

      const body = {
        email: email,
        columns: items,
      };

      await exportService.sendTrafficCsvReport(pageLimit, tableFilters, tableSortBy, searchValue, body, isArchive);

      notification.success({
        message: 'The exported CSV file you have requested is being sent to the specified email address.',
        duration: 10,
      });
    },
  });

  const onExportCsvClicked = async () => {
    const { pageLimit, tableFilters, tableSortBy } = getTableFilters();

    const { from, to } = getFilterDateRange(tableFilters);

    const dateFrom = from ? dayjs(Number(from)) : dayjs().subtract(1, 'year');
    const dateTo = to ? dayjs(Number(to)) : dayjs();
    const isMoreThan7Days = dateTo.diff(dateFrom, 'day') > 7;

    if (isMoreThan7Days) {
      sendTrafficCsvReportModal.showModal();
    } else {
      const items = getColumns();
      const blob = await exportService.processTrafficLogs(pageLimit, tableFilters, tableSortBy, searchValue, { columns: items }, isArchive);
      downloadCsv(blob, gatFileName(tableFilters));
    }
  };

  const location = useLocation();

  let items: MenuProps['items'] = [
    {
      label: <SH.HotkeyLink to={`/${basePath}/traffic`} label={'Traffic'} />,
      key: `/${basePath}/traffic`,
    },
    {
      label: <SH.HotkeyLink to={`/${basePath}/traffic/archive`} label={'Archived traffic'} />,
      key: `/${basePath}/traffic/archive`,
    },
  ];

  const getSelectedKey = () => {
    const sortedItems = [...(items || [])].sort((a, b) => (String(b?.key).length || 0) - (String(a?.key).length || 0));
    for (const item of sortedItems) {
      if (item && item.key && location.pathname.startsWith(String(item.key))) {
        return item.key as string;
      }
    }

    return `/${basePath}/traffic`;
  };

  const selectedKey = getSelectedKey();

  return (
    <>
      <div className="eiq-secure-traffic-page">
        <Menu
          defaultSelectedKeys={[location.pathname]}
          className="cc-menu-item traffic-log-menu"
          items={items}
          mode="horizontal"
          selectedKeys={[selectedKey]}
        />
        <Row gutter={16} className="traffic-content">
          <Col flex="auto">
            <Card className="eiq-card eiq-card-border" style={{ width: '100%' }}>
              <FormWrapper form={form} onFinish={onFinish} initialValues={initialValues} layout="vertical">
                <Row justify="space-between" className="content-title">
                  <Space size={12}>
                    <h2 className="main-title">
                      {isArchive
                        ? 'This section contains archived traffic data.'
                        : 'This section contains active traffic data of the last 3 months.'}
                    </h2>
                  </Space>
                  {!isMobile && (
                    <div className="action-buttons">
                      <Button
                        disabled={!tableControls.getFilters()}
                        type="primary"
                        size="middle"
                        onClick={onExportCsvClicked}
                        className="mobile-btn">
                        Export CSV
                      </Button>
                      <Button htmlType="submit" type="primary" className="css-primary-brn" icon={<ArrowRight />}>
                        Run Filter
                      </Button>
                    </div>
                  )}
                </Row>

                <Row gutter={{ lg: 32, md: 16, xs: 0 }}>
                  <Col xs={24} lg={8}>
                    <Form.Item name="dateFrom" label="Date from">
                      <EiqDatePicker
                        format={DATE_MMMM_D_YYYY_H_MMA}
                        showTime={{
                          format: 'hh:mm A',
                          use12Hours: true,
                          defaultValue: dayjs('24:00', 'HH:mm'),
                        }}
                        placeholder="Start date"
                        allowClear={false}
                      />
                    </Form.Item>
                    <Form.Item name="dateTo" label="Date to">
                      <EiqDatePicker
                        format={DATE_MMMM_D_YYYY_H_MMA}
                        showTime={{
                          format: 'hh:mm A',
                          use12Hours: true,
                          defaultValue: dayjs('24:00', 'HH:mm'),
                        }}
                        placeholder="End date"
                        allowClear={false}
                      />
                    </Form.Item>
                    {isDesktop && (
                      <Form.Item name="carLicensePlate" label="Filter by License Plate">
                        <Input placeholder="Filter by license plate" />
                      </Form.Item>
                    )}
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item name="visitorType" label="Visitor types">
                      <TreeSelect
                        popupClassName="eiq-select-days so-dropdown"
                        treeDefaultExpandAll={true}
                        treeCheckable={true}
                        treeData={visitorTypeTreeOptions}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        maxTagCount={1}
                        filterTreeNode={(search, item) => {
                          return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                        }}
                        showSearch={true}
                        placeholder="All"
                      />
                    </Form.Item>

                    <Form.Item name="admittanceType" label="Admittance">
                      <TreeSelect
                        popupClassName="eiq-select-days so-dropdown"
                        treeDefaultExpandAll={true}
                        treeCheckable={true}
                        treeData={admittanceTreeOptions}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        maxTagCount={1}
                        filterTreeNode={(search, item) => {
                          return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                        }}
                        showSearch={true}
                        placeholder="All"
                      />
                    </Form.Item>

                    <Form.Item name="turnBackReason" label="Reason for turnback">
                      <TreeSelect
                        popupClassName="eiq-select-days so-dropdown"
                        treeDefaultExpandAll={true}
                        treeCheckable={true}
                        treeData={reasonsOptions}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        maxTagCount={1}
                        filterTreeNode={(search, item) => {
                          return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                        }}
                        showSearch={true}
                        placeholder="All reasons"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item name="gate" label="Location">
                      <TreeSelect
                        popupClassName="eiq-select-days so-dropdown"
                        treeDefaultExpandAll={true}
                        treeCheckable={true}
                        treeData={gateOptions}
                        onChange={handleGateChange}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        maxTagCount={1}
                        filterTreeNode={(search, item) => {
                          return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                        }}
                        showSearch={true}
                        placeholder="All"
                      />
                    </Form.Item>

                    {showCamera && (
                      <Form.Item name="camera" label="Camera">
                        <TreeSelect
                          popupClassName="eiq-select-days so-dropdown"
                          treeDefaultExpandAll={true}
                          treeCheckable={true}
                          treeData={cameraOptions}
                          showCheckedStrategy={TreeSelect.SHOW_PARENT}
                          maxTagCount={1}
                          filterTreeNode={(search, item) => {
                            return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                          }}
                          showSearch={true}
                          placeholder="All"
                        />
                      </Form.Item>
                    )}

                    <Form.Item name="direction" label="Direction">
                      <TreeSelect
                        popupClassName="eiq-select-days so-dropdown"
                        treeDefaultExpandAll={true}
                        treeCheckable={true}
                        treeData={directionTreeOptions}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        maxTagCount={1}
                        filterTreeNode={(search, item) => {
                          return (item?.title as string)?.toLowerCase()?.indexOf(search.toLowerCase()) >= 0;
                        }}
                        showSearch={true}
                        placeholder="All"
                      />
                    </Form.Item>
                    {!isDesktop && (
                      <Form.Item name="carLicensePlate" label="Filter by License Plate">
                        <Input placeholder="Filter by license plate" />
                      </Form.Item>
                    )}
                  </Col>
                </Row>

                <Row className="eiq-secure-traffic-result">Search in Log</Row>

                {isMobile && (
                  <div className="mobile-footer">
                    <Button htmlType="submit" type="primary" className="css-primary-brn w-100" icon={<ArrowRight />}>
                      Run Filter
                    </Button>
                    <Button
                      disabled={!tableControls.getFilters()}
                      style={{ marginTop: '6px' }}
                      onClick={onExportCsvClicked}
                      type="primary"
                      className="css-primary-brn w-100">
                      Export CSV
                    </Button>
                  </div>
                )}
              </FormWrapper>
              <TableContextProvider controls={tableControls}>
                <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
                  <Col flex="auto">
                    <SearchFilter
                      value={searchValue}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder={`Search by resident's name, address, guest's name${
                        featureStore.isSmartPlateIdEnabled ? ', smartPlate ID' : ''
                      }, device number, company name`}
                      //if need update search rule on backend
                      rulesForColumn={
                        {
                          // residentName: FilterTypesEnum.Contains,
                          // address: FilterTypesEnum.Contains,
                          // guestName: FilterTypesEnum.Contains,
                          // ...(featureStore.isSmartPlateIdEnabled && { smartPlateId: FilterTypesEnum.StartWith }),
                          // deviceId: FilterTypesEnum.StartWith,
                          // companyName: FilterTypesEnum.StartWith,
                        }
                      }
                    />
                  </Col>
                  <ColumnVisibility isColumnReorderAllow={true} />
                </Row>
                <TableBase minHeight={'200px'} rowClassName={getRowClassName} />
              </TableContextProvider>
            </Card>
          </Col>
        </Row>
      </div>
      <TrafficReportExportModal
        initData={sendTrafficCsvReportModal.initData}
        isOpen={sendTrafficCsvReportModal.isOpenModal}
        title={`Traffic Report Export`}
        onClose={sendTrafficCsvReportModal.hideModal}
      />
    </>
  );
};
